import { Component, Input } from '@angular/core';
import { RevenueComponent } from '@vendasta/sales-orders';
import { BehaviorSubject } from 'rxjs';
import { SummaryItem, TaxOption, UILineItem } from '../../order-form/order-item-list/interface';
import { OrderSummaryService } from '../order-summary.service';
import { Frequency } from '@vendasta/galaxy/frequency';

interface OrderTotals {
  total: number;
  subtotal: number;
  taxes: OrderTax[];
  currency: string;
}

interface OrderTax {
  tax: number;
  percentage: string;
}

@Component({
  selector: 'store-order-summarized-totals',
  templateUrl: './order-summarized-totals.component.html',
  styleUrls: ['./order-summarized-totals.component.scss'],
})
export class OrderSummarizedTotalsComponent {
  @Input()
  set orderItems(orderItems: UILineItem[]) {
    const orderSummary = OrderSummaryService.getPriceSummary(orderItems);
    this._orderItems = orderSummary.items;
    this.orderTotals$$.next(this.calculateOrderTotals(this._orderItems, this._taxOptions));
  }

  @Input()
  set taxOptions(taxOptions: TaxOption[]) {
    this._taxOptions = taxOptions;
    this.orderTotals$$.next(this.calculateOrderTotals(this._orderItems, this._taxOptions));
  }

  private _orderItems: SummaryItem[];
  private _taxOptions: TaxOption[];

  private orderTotals$$ = new BehaviorSubject<OrderTotals>(null);
  public orderTotals$ = this.orderTotals$$.asObservable();

  private calculateOrderTotals(orderItems: SummaryItem[], taxOptions: TaxOption[]): OrderTotals {
    let orderTotal = 0;
    let orderSubtotal = 0;
    let orderTaxes: OrderTax[] = [];
    orderItems.forEach((orderItem) => {
      const prices = orderItem.revenueComponents;
      orderTotal += this.calculateTotal(prices, taxOptions);
      orderSubtotal += this.calculateSubtotal(prices);
      const itemTaxes = this.calculateTaxes(prices, taxOptions);
      if (orderTaxes.length > 0) {
        orderTaxes = orderTaxes.map((tax, i) => ({
          tax: tax.tax + itemTaxes[i].tax,
          percentage: tax.percentage,
        }));
      } else {
        orderTaxes = itemTaxes;
      }
    });
    const orderTotals = {
      total: orderTotal,
      subtotal: orderSubtotal,
      taxes: orderTaxes,
      currency: orderItems.length > 0 ? orderItems[0].currencyCode : null,
    };
    this.convertTotalsToDollars(orderTotals);
    return orderTotals;
  }

  private calculateTotal(prices: RevenueComponent[], taxOptions: TaxOption[]): number {
    const subtotal = this.calculateSubtotal(prices);
    const taxes = this.calculateTaxes(prices, taxOptions);
    return subtotal + taxes.reduce((total, tax) => total + tax.tax, 0);
  }

  private calculateSubtotal(prices: RevenueComponent[]): number {
    return (prices || []).reduce((subtotal, price) => subtotal + price.value, 0);
  }

  private calculateTaxes(prices: RevenueComponent[], taxOptions: TaxOption[]): OrderTax[] {
    return (taxOptions || []).map((taxOption) => {
      const tax = (prices || []).reduce(
        (totalTax, price) => totalTax + price.value * taxOption.percentageMultiplier,
        0,
      );
      return {
        tax: tax,
        percentage: (taxOption.percentageMultiplier * 100).toFixed(2),
      };
    });
  }

  private convertTotalsToDollars(orderTotals: OrderTotals): void {
    orderTotals.total /= 100;
    orderTotals.subtotal /= 100;
    orderTotals.taxes.forEach((tax) => {
      tax.tax /= 100;
    });
  }

  protected readonly Frequency = Frequency;
}
